/*
  Virasat hero carousel (sections/virasat-hero.liquid).
  Arrows, dots, swipe and optional autoplay. Autoplay stops on hover, on keyboard focus,
  when the tab is hidden, when the shopper presses pause, and for people who ask for reduced motion.
  In the theme editor, selecting a slide block shows that slide.
*/
if (!customElements.get('virasat-hero')) {
  class VirasatHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (this.slides.length < 2) return;

      this.index = 0;
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.counter = this.querySelector('[data-counter]');
      this.live = this.querySelector('[aria-live]');
      this.pauseButton = this.querySelector('[data-pause]');
      this.delay = Number(this.dataset.autoplay || 0) * 1000;
      this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.hovered = false;
      this.focused = false;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.update();
      });

      this.addEventListener('mouseenter', () => { this.hovered = true; this.update(); });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.update(); });
      this.addEventListener('focusin', () => { this.focused = true; this.update(); });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) { this.focused = false; this.update(); }
      });
      this.onVisibility = () => this.update();
      document.addEventListener('visibilitychange', this.onVisibility);

      this.addEventListener('touchstart', (event) => {
        this.touchX = event.touches[0].clientX;
        this.touchY = event.touches[0].clientY;
      }, { passive: true });
      this.addEventListener('touchend', (event) => {
        if (this.touchX == null) return;
        const dx = event.changedTouches[0].clientX - this.touchX;
        const dy = event.changedTouches[0].clientY - this.touchY;
        this.touchX = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      this.onBlockSelect = (event) => {
        const i = this.slides.findIndex((slide) => slide.contains(event.target));
        if (i < 0) return;
        this.editorHold = true;
        this.go(i);
      };
      this.onBlockDeselect = () => { this.editorHold = false; this.update(); };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.update();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    go(target) {
      const count = this.slides.length;
      this.index = (target + count) % count;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
      this.update();
    }

    // Start or stop the timer, and keep the pause button and live region in step with it.
    update() {
      const running = this.delay > 0 && !this.userPaused && !this.hovered && !this.focused
        && !this.editorHold && document.visibilityState === 'visible';

      clearInterval(this.timer);
      if (running) this.timer = setInterval(() => this.go(this.index + 1), this.delay);

      // Screen readers hear slide changes only when the shopper is in control
      if (this.live) this.live.setAttribute('aria-live', running ? 'off' : 'polite');

      if (this.pauseButton) {
        this.pauseButton.toggleAttribute('data-paused', this.userPaused);
        this.pauseButton.setAttribute(
          'aria-label',
          this.userPaused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause
        );
      }
    }
  }

  customElements.define('virasat-hero', VirasatHero);
}
